import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_BASE_URL = '/api/v1';

export interface RunnerSummary {
  id: string;
  name: string;
  status: string;
  isOnline: boolean;
}

export interface ServerSummary {
  id: string;
  name: string;
  status: string;
  isOnline: boolean;
}

export interface SystemHealthSummary {
  onlineRunners: number;
  offlineRunners: number;
  degradedRunners: number;
  onlineServers: number;
  offlineServers: number;
  degradedServers: number;
}

export interface UseSystemHealthReturn {
  runners: RunnerSummary[];
  servers: ServerSummary[];
  summary: SystemHealthSummary;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

function mapStatus(status: string | null | undefined): boolean {
  return status === 'active' || status === 'online';
}

export function useSystemHealth(): UseSystemHealthReturn {
  const { isAuthenticated } = useAuth();
  const [runners, setRunners] = useState<RunnerSummary[]>([]);
  const [servers, setServers] = useState<ServerSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchHealth = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const [runnerRes, serverRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/tool-runners`),
        axios.get(`${API_BASE_URL}/media-servers`),
      ]);

      const runnerItems = runnerRes.data?.data?.items ?? runnerRes.data?.data ?? [];
      const serverItems = serverRes.data?.data?.items ?? serverRes.data?.data ?? [];

      setRunners(
        runnerItems.map((r: any) => ({
          id: r.id ?? r.runnerId,
          name: r.name ?? 'Unknown Runner',
          status: r.status ?? 'unknown',
          isOnline: mapStatus(r.status),
        }))
      );

      setServers(
        serverItems.map((s: any) => ({
          id: s.id,
          name: s.name ?? 'Unknown Server',
          status: s.status ?? 'unknown',
          isOnline: mapStatus(s.status),
        }))
      );

      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message ?? 'Failed to load system health');
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // Initial fetch
  useEffect(() => {
    fetchHealth();
  }, [fetchHealth]);

  // 30-second polling
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      fetchHealth();
    }, 30000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchHealth]);

  const summary: SystemHealthSummary = {
    onlineRunners: runners.filter((r) => r.isOnline).length,
    offlineRunners: runners.filter((r) => !r.isOnline).length,
    degradedRunners: 0,
    onlineServers: servers.filter((s) => s.isOnline).length,
    offlineServers: servers.filter((s) => !s.isOnline).length,
    degradedServers: 0,
  };

  return {
    runners,
    servers,
    summary,
    isLoading,
    error,
    refetch: fetchHealth,
  };
}
